import { ref } from 'vue'

/**
 * Whether the hotel has switched online check-in on at all.
 *
 * This portal is the face of the `guest` module, and from this release the API refuses
 * every route of a module the hotel has not bought with 403 and
 * `{"error": {"code": 19}}` (docs/MODULI.md §3.2, `pms-api/pkg/enums/error`). It is a
 * 403 like an ordinary refusal, and the code is the only thing that tells them apart.
 *
 * Without this a guest whose hotel has not bought the module gets the dead-link screen
 * — "ask the reception for a new link" — and rings a desk that will send them the same
 * link again. The link is fine; the hotel does not offer this.
 *
 * A module-level ref rather than a field on the stay store, for the same reason
 * `libs/toast.ts` is one: `libs/api.ts` is what sees the refusal, and it is called from
 * the router guard as well as from screens, where no store may be resolved yet.
 *
 * The notice carries no retry button and nothing clears this on a timer: a licence is
 * changed at the hotel's vendor console and not by a guest pressing a button in a car
 * park.
 *
 * What does clear it is a different token in the address. The token IS the identity
 * here — there is no sign-in to change — so a refusal recorded for one link must not
 * outlive it: without this, opening a second hotel's link in the same tab showed the
 * first hotel's refusal, and so did a link opened after the hotel switched check-in
 * on. A full load starts clear anyway, this ref living only as long as the page.
 */
export const moduleUnlicensed = ref(false)

/**
 * Forgets a refusal, because the link it was recorded for is not the one being opened.
 *
 * Called from `stores/fetch.ts` when a link stashes a token that differs from the one
 * held. Re-opening the SAME link does not clear it: that would ask the same locked
 * door the same question, which is the loop this flag exists to avoid.
 */
export const clearModuleRefusal = () => {
    moduleUnlicensed.value = false
}

/** The API's code for a module this hotel has not bought. */
const MODULE_NOT_LICENSED = 19

/** Reads one refused answer and says whether it was this refusal. */
export const noteModuleRefusal = (status: number, body: unknown): boolean => {
    if (status !== 403) return false

    if ((body as { error?: { code?: number } })?.error?.code !== MODULE_NOT_LICENSED) return false

    moduleUnlicensed.value = true

    return true
}
